import { IDoFilter } from '@app/main/main-content/display-options/interfaces/do-filter.interface';
import { ILdLoggerFilters } from '@app/main/leak-detection/interfaces/ld-logger-filters';
import {
  CorrelationOptionKey,
  DoFilterOptionKey,
  EventFilterRowKey,
  LoggerOtherOptionKey,
  LoggersByAiPredictorOptionKey,
  LoggerTypeOptionKey,
} from '@app/main/main-content/display-options/enums/do-filter-option-key.enum';
import { isOptionSelected } from '@app/main/main-content/display-options/utils/do-filter.utils';
import { getEventFilterLeafKey } from '@app/main/main-content/display-options/utils/do-event-filter.utils';
import { CorrelationType } from '@app/core/enums/correlation-type.enum';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IEvent } from '@app/api/interfaces/event.interface';
import { deriveLdEventVisibility } from '@app/main/leak-detection/utils/ld-event-status';
import { LdEventVisibility } from '@app/main/leak-detection/enums/ld-event-visibility.enum';
import { LeakDetectionStatus } from '@app/core/enums/leak-detection-status.enum';
import { CommonStateStatus } from '@app/core/enums/common-state-status.enum';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';
import { getLoggerMeasurementPrediction } from '@app/main/leak-detection/utils/logger-measurement-prediction';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { isLoggerNotAssignedToPipeNode } from '@app/core/methods/logger-pipe-assignment';

const LEAK_SCORE_OPTION_KEYS = new Set<string>(Object.values(LoggerTypeOptionKey));
const AI_PREDICTOR_OPTION_KEYS = new Set<string>(Object.values(LoggersByAiPredictorOptionKey));

function isLeakScoreOptionKey(key: DoFilterOptionKey): boolean {
  return LEAK_SCORE_OPTION_KEYS.has(key);
}

function isAiPredictorOptionKey(key: DoFilterOptionKey): boolean {
  return AI_PREDICTOR_OPTION_KEYS.has(key);
}

/** Developer's combined filter carries both key sets; only one of the two is ever populated otherwise. */
export function getActiveLoggerFilter({ loggerType, loggersByAi }: ILdLoggerFilters): IDoFilter | undefined {
  return loggerType?.enabled ? loggerType : loggersByAi?.enabled ? loggersByAi : undefined;
}

export function isLoggerVisible(loggerFilters: ILdLoggerFilters, logger: ILdLogger): boolean {
  const { leakDetectionMode } = loggerFilters;
  const filter = getActiveLoggerFilter(loggerFilters);

  if (!filter) {
    return false;
  }

  if (
    isOptionSelected(filter, LoggerOtherOptionKey.NotAssignedToPipeNode) &&
    !isLoggerNotAssignedToPipeNode(logger.deployment.adjustedCoordinates)
  ) {
    return false;
  }

  // A temporary position replaces the leak-score marker entirely, so for such loggers the `Other`
  // option is the only thing that decides visibility.
  if (logger.deployment.isPositionTemporary) {
    return isOptionSelected(filter, LoggerOtherOptionKey.TemporaryPositioned);
  }

  // A filter offering both key sets (Developer) requires a match in each — AND, not a union.
  const offersLeakScore = filter.options.some(option => isLeakScoreOptionKey(option.key));
  const offersAiPredictor = filter.options.some(option => isAiPredictorOptionKey(option.key));

  const leakScoreColumnMode = offersAiPredictor ? LeakDetectionMode.LeakScore : leakDetectionMode;

  const matchesLeakScore =
    !offersLeakScore ||
    filter.selected.some(
      option =>
        isLeakScoreOptionKey(option.key) && matchesPredictionOption(option.key, logger.measurement, leakScoreColumnMode)
    );
  const matchesAiPredictor =
    !offersAiPredictor ||
    filter.selected.some(
      option =>
        isAiPredictorOptionKey(option.key) &&
        matchesPredictionOption(option.key, logger.measurement, LeakDetectionMode.LeakAiPredictor)
    );

  return matchesLeakScore && matchesAiPredictor;
}

function matchesPredictionOption(
  optionKey: DoFilterOptionKey,
  measurement: ILoggerMeasurement | undefined,
  leakDetectionMode: LeakDetectionMode
): boolean {
  const { status } = getLoggerMeasurementPrediction(measurement, leakDetectionMode);

  switch (optionKey) {
    case LoggerTypeOptionKey.Probable:
    case LoggersByAiPredictorOptionKey.Probable:
      return status === LeakDetectionStatus.Critical;
    case LoggerTypeOptionKey.Possible:
    case LoggersByAiPredictorOptionKey.Possible:
      return status === LeakDetectionStatus.Warn;
    case LoggerTypeOptionKey.NoLeak:
    case LoggersByAiPredictorOptionKey.NoLeak:
      return status === LeakDetectionStatus.Normal;
    case LoggerTypeOptionKey.NoData:
    case LoggersByAiPredictorOptionKey.NoData:
      // A missing status renders as a neutral pin, so it counts as No data.
      return !status || status === LeakDetectionStatus.Neutral;
    default:
      return false;
  }
}

export function isCorrelationVisible(filter: IDoFilter | undefined, correlation: ICorrelation): boolean {
  if (!filter?.enabled) {
    return false;
  }

  if (!matchesSelectedCorrelationTypes(filter, correlation)) {
    return false;
  }

  return true;
}

function matchesSelectedCorrelationTypes(filter: IDoFilter, correlation: ICorrelation): boolean {
  const selectedKeys = new Set((filter.selected ?? []).map(o => o.key as CorrelationOptionKey));

  const typeKeyToCorrelationType: Partial<Record<CorrelationOptionKey, CorrelationType>> = {
    [CorrelationOptionKey.WithPipeInfo]: CorrelationType.Pinpoint,
    [CorrelationOptionKey.WithoutPipeInfo]: CorrelationType.WithoutPipe,
    [CorrelationOptionKey.OutOfBracket]: CorrelationType.OutOfBracket,
    [CorrelationOptionKey.Unsynchronized]: CorrelationType.Unsynced,
  };

  const typeKeys = Object.keys(typeKeyToCorrelationType) as CorrelationOptionKey[];

  const activeTypeKeys = typeKeys.filter(k => selectedKeys.has(k));

  if (!activeTypeKeys.some(k => correlation.correlationType === typeKeyToCorrelationType[k])) {
    return false;
  }

  if (correlation.suppressed) {
    return selectedKeys.has(CorrelationOptionKey.Suppressed);
  }

  const qualityKeyToStatus: Partial<Record<CorrelationOptionKey, CommonStateStatus>> = {
    [CorrelationOptionKey.Probable]: CommonStateStatus.Critical,
    [CorrelationOptionKey.Possible]: CommonStateStatus.Warning,
  };

  const activeQualityKeys = (Object.keys(qualityKeyToStatus) as CorrelationOptionKey[]).filter(k =>
    selectedKeys.has(k)
  );

  return activeQualityKeys.some(k => correlation.qualityStatus === qualityKeyToStatus[k]);
}

export function isEventVisible(filter: IDoFilter | undefined, event: IEvent): boolean {
  if (!filter?.enabled) return false;

  // Hidden events are only shown while the dedicated row is ticked. That row is not one of the
  // filter's options, so it lives in the tree rather than in `selected`.
  if (deriveLdEventVisibility(event) === LdEventVisibility.Hidden && !showHiddenEvents(filter)) return false;

  const leafKey = getEventFilterLeafKey(event);
  if (!leafKey) return false;

  return !!filter.selected?.some(option => option.key === leafKey);
}

function showHiddenEvents(filter: IDoFilter): boolean {
  return !!filter.sections?.flat().find(item => item.key === EventFilterRowKey.ShowHiddenEvents)?.checked;
}
